import { useState, useRef, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import type { StripeDailyBalance } from '@/lib/stripeService';

interface DayPickerModalProps {
  selectedDate: string;
  dailyBalances: StripeDailyBalance[];
  onSelect: (date: string) => void;
  onClose: () => void;
}

export default function DayPickerModal({ selectedDate, dailyBalances, onSelect, onClose }: DayPickerModalProps) {
  const initial = new Date(selectedDate + 'T12:00:00');
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);

  const balanceMap = new Map<string, StripeDailyBalance>();
  for (const b of dailyBalances) {
    balanceMap.set(b.date, b);
  }

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const monthName = new Date(viewYear, viewMonth, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(viewYear - 1); setViewMonth(11); }
    else setViewMonth(viewMonth - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(viewYear + 1); setViewMonth(0); }
    else setViewMonth(viewMonth + 1);
  };

  const formatDay = (day: number) => {
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${viewYear}-${m}-${d}`;
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      selectedRef.current?.scrollIntoView({ block: 'center', behavior: 'instant' });
    });
  }, [viewMonth, viewYear]);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="glass-modal rounded-2xl w-full max-w-sm flex flex-col"
        style={{ maxHeight: '70vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-4 py-3 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-teal-500/10 flex items-center justify-center">
              <Calendar size={14} className="text-teal-400" />
            </div>
            <h2 className="text-sm font-bold text-white">Daily Sales</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg glass-hover">
            <X size={16} className="text-white" />
          </button>
        </div>

        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 shrink-0">
          <button onClick={prevMonth} className="p-1 rounded-lg glass-hover">
            <ChevronLeft size={14} className="text-white" />
          </button>
          <span className="text-xs font-semibold text-white">{monthName}</span>
          <button onClick={nextMonth} className="p-1 rounded-lg glass-hover">
            <ChevronRight size={14} className="text-white" />
          </button>
        </div>

        <div ref={scrollRef} className="overflow-y-auto flex-1 py-1">
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
            const dateStr = formatDay(day);
            const data = balanceMap.get(dateStr);
            const isSelected = dateStr === selectedDate;
            const isCurrentDay = dateStr === todayStr;
            const isFuture = dateStr > todayStr;
            const dayDate = new Date(dateStr + 'T12:00:00');
            const dayLabel = dayDate.toLocaleDateString('en-US', { weekday: 'short' });

            return (
              <button
                key={dateStr}
                ref={isSelected ? selectedRef : undefined}
                onClick={() => !isFuture && onSelect(dateStr)}
                disabled={isFuture}
                className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${
                  isFuture
                    ? 'opacity-30 cursor-not-allowed'
                    : isSelected
                      ? 'bg-teal-500/20'
                      : 'hover:bg-white/5 cursor-pointer'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex flex-col items-center justify-center shrink-0 ${
                  isSelected ? 'bg-teal-500' : isCurrentDay ? 'bg-teal-500/20' : 'bg-white/5'
                }`}>
                  <span className={`text-[10px] leading-none font-medium ${
                    isSelected ? 'text-white/70' : isCurrentDay ? 'text-teal-400' : 'text-white/40'
                  }`}>{dayLabel}</span>
                  <span className={`text-xs font-bold leading-tight ${
                    isSelected ? 'text-white' : isCurrentDay ? 'text-teal-400' : 'text-white'
                  }`}>{day}</span>
                </div>

                <div className="flex-1 min-w-0 text-left">
                  {data && data.count > 0 ? (
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm font-bold text-white tabular-nums">
                        ${(data.gross / 100).toFixed(2)}
                      </span>
                      <span className="text-[10px] text-white/40 tabular-nums">
                        {data.count} txn{data.count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-white/30">{isFuture ? '--' : 'No sales'}</span>
                  )}
                </div>

                {isSelected && data && data.count > 0 && (
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
